/**
 * Shared date + step logic for the number-porting progress banner.
 * Loaded before np-progress-banner inline Alpine setup; exposed on globalThis.
 */
(function (g) {
  'use strict';

  /** Show “completed” banner for 24h after end of the confirmed port calendar day (see toCompletionAnchorMs). */
  var PORTING_COMPLETED_BANNER_MS = 24 * 60 * 60 * 1000;

  /**
   * End of local calendar day (23:59:59.999) for the confirmed port date, used before adding 24h.
   * Date-only `YYYY-MM-DD` uses that local day; ISO strings use the local calendar day of the parsed instant.
   *
   * @param {unknown} value
   * @returns {number} ms at 23:59:59.999 local, or NaN
   */
  function toCompletionAnchorMs(value) {
    if (value == null || value === '') return NaN;

    var s = String(value).trim();
    var dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);

    var y;
    var mo;
    var d;

    if (dateOnly) {
      y = parseInt(dateOnly[1], 10);
      mo = parseInt(dateOnly[2], 10) - 1;
      d = parseInt(dateOnly[3], 10);
      if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return NaN;

      var probe = new Date(y, mo, d);
      if (probe.getFullYear() !== y || probe.getMonth() !== mo || probe.getDate() !== d) return NaN;
    } else {
      var parsed = new Date(s);
      if (Number.isNaN(parsed.getTime())) return NaN;
      y = parsed.getFullYear();
      mo = parsed.getMonth();
      d = parsed.getDate();
    }

    return new Date(y, mo, d, 23, 59, 59, 999).getTime();
  }

  /** Works across realms (e.g. vm tests) where `instanceof Date` fails. */
  function isCoercibleDate(value) {
    return value != null && typeof value.getTime === 'function' && !Number.isNaN(value.getTime());
  }

  /**
   * @param {object|undefined|null} porting
   * @param {Date} [now]
   * @returns {boolean}
   */
  function isPortingCompletedWithinBannerWindow(porting, now) {
    if (!porting || porting.status !== 'completed' || !porting.confirmedPortDate) return false;

    var anchor = toCompletionAnchorMs(porting.confirmedPortDate);
    if (Number.isNaN(anchor)) return false;

    var t = isCoercibleDate(now) ? now.getTime() : new Date().getTime();
    return t >= anchor && t < anchor + PORTING_COMPLETED_BANNER_MS;
  }

  /**
   * Parse API date strings into local calendar components.
   * Date-only `YYYY-MM-DD` is interpreted as local midnight (avoids UTC day shift).
   * ISO datetimes use the local calendar day of the parsed instant.
   *
   * @param {unknown} value
   * @returns {{ y: number, mo: number, d: number } | null} mo is 0–11
   */
  function toLocalDateParts(value) {
    if (value == null || value === '') return null;

    const s = String(value).trim();
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);

    if (dateOnly) {
      const y = parseInt(dateOnly[1], 10);
      const mo = parseInt(dateOnly[2], 10) - 1;
      const d = parseInt(dateOnly[3], 10);

      if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return null;

      const probe = new Date(y, mo, d);
      if (probe.getFullYear() !== y || probe.getMonth() !== mo || probe.getDate() !== d) return null;

      return { y: y, mo: mo, d: d };
    }

    const t = new Date(s);
    if (Number.isNaN(t.getTime())) return null;

    return { y: t.getFullYear(), mo: t.getMonth(), d: t.getDate() };
  }

  /**
   * @param {unknown} value
   * @param {Date} [now] defaults to current time (override in tests)
   * @returns {boolean}
   */
  function isLocalCalendarDayToday(value, now) {
    const n = isCoercibleDate(now) ? now : new Date();
    const parts = toLocalDateParts(value);
    if (!parts) return false;

    return parts.y === n.getFullYear() && parts.mo === n.getMonth() && parts.d === n.getDate();
  }

  /**
   * Sort key for confirmed port dates (newest local calendar day first).
   * @param {unknown} a
   * @param {unknown} b
   * @returns {number}
   */
  function compareConfirmedPortDateDesc(a, b) {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;

    const pa = toLocalDateParts(a);
    const pb = toLocalDateParts(b);

    if (!pa && !pb) return 0;
    if (!pa) return 1;
    if (!pb) return -1;

    return new Date(pb.y, pb.mo, pb.d).getTime() - new Date(pa.y, pa.mo, pa.d).getTime();
  }

  /**
   * @param {unknown} value
   * @returns {string|null}
   */
  function formatPortDateDisplay(value) {
    const parts = toLocalDateParts(value);
    if (!parts) return null;

    return new Date(parts.y, parts.mo, parts.d).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /**
   * @param {string} portingStatus
   * @param {unknown} confirmedPortDateRaw
   * @param {Date} [now] defaults to current time (override in tests)
   * @returns {'requested'|'confirmed'|'transfer'|'completed'}
   */
  function activeProgressStep(portingStatus, confirmedPortDateRaw, now) {
    const n = isCoercibleDate(now) ? now : new Date();
    switch (portingStatus) {
      case 'requested':
        return 'requested';
      case 'completed':
        return 'completed';
      case 'started':
        return 'transfer';
      case 'scheduled':
        if (confirmedPortDateRaw && isLocalCalendarDayToday(confirmedPortDateRaw, n)) return 'transfer';
        return 'confirmed';
      default:
        return 'requested';
    }
  }

  g.NpProgressBannerLogic = {
    toLocalDateParts: toLocalDateParts,
    isLocalCalendarDayToday: isLocalCalendarDayToday,
    compareConfirmedPortDateDesc: compareConfirmedPortDateDesc,
    formatPortDateDisplay: formatPortDateDisplay,
    activeProgressStep: activeProgressStep,
    toCompletionAnchorMs: toCompletionAnchorMs,
    isPortingCompletedWithinBannerWindow: isPortingCompletedWithinBannerWindow,
  };

  g.isPortingCompletedWithinBannerWindow = isPortingCompletedWithinBannerWindow;
})(typeof globalThis !== 'undefined' ? globalThis : this);
